import { useLayoutEffect, useRef, useState } from 'react';

/**
 * The width of the element the chart is drawn into, kept current.
 *
 * The charts lay out in real pixels rather than scaling a fixed viewBox, so
 * the axis text stays 12px on a phone instead of shrinking with the plot.
 * Until the first measurement — and in a test renderer with no layout —
 * `fallback` is used.
 */
export function useContainerWidth<T extends HTMLElement>(fallback = 640) {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(fallback);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => {
      const w = Math.round(el.getBoundingClientRect().width);
      if (w > 0) setWidth(w);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return { ref, width };
}
